/**
 * O mapa de uma automação: o grafo do serviço em blocos e ligações para a tela.
 *
 * Sem React e sem a biblioteca do mapa: daqui saem os blocos (com título,
 * resumo e grupo), as ligações (com o rótulo da saída) e as posições. O
 * componente só entrega isso ao React Flow.
 *
 * ── O começo ──────────────────────────────────────────────────────────────
 *
 * No serviço, o gatilho e a condição ficam fora do grafo. Na tela, eles são o
 * primeiro bloco ("Começa quando"), ligado ao início do grafo, como no desenho.
 *
 * ── Organizar ─────────────────────────────────────────────────────────────
 *
 * O bloco sem posição guardada ganha uma pela árvore, da esquerda para a
 * direita a partir do começo, como o "Organizar" do protótipo: uma coluna por
 * nível, o pai no meio dos filhos, e os soltos empilhados embaixo.
 */

import type { AutomacaoDaApi, BlocosDoEditor } from '@cliqcrm/contracts';
import { rotuloDoResultado } from './resultado-da-consulta';

type Grafo = NonNullable<AutomacaoDaApi['grafo']>;
export type NoDoGrafo = Grafo['nos'][number];
export type LigacaoDoGrafo = Grafo['ligacoes'][number];

/** O grupo dá a cor do bloco: de onde ele vem no catálogo. */
export type GrupoDoBloco = 'comeco' | 'acao' | 'logica' | 'espera' | 'dados';

export type Bloco = {
  id: string;
  grupo: GrupoDoBloco;
  titulo: string;
  resumo: string;
  /** As saídas que o bloco tem, na ordem em que aparecem. */
  saidas: string[];
  x: number;
  y: number;
};

export type Ligacao = { id: string; de: string; para: string; rotulo: string | null };

export const ID_DO_COMECO = 'comeco';

/**
 * Os outros começos (#1183) são blocos da tela, fora do grafo: `comeco-1` é o
 * primeiro de `outros_comecos`, `comeco-2` o segundo. O contrato diz o bloco de
 * entrada de cada um; a posição deles não se guarda.
 */
export function idDoOutroComeco(posicao: number): string {
  return `${ID_DO_COMECO}-${posicao + 1}`;
}

/** A posição em `outros_comecos` do bloco, ou `null` se ele não for um outro começo. */
export function posicaoDoOutroComeco(id: string): number | null {
  const m = /^comeco-(\d+)$/.exec(id);
  return m ? Number(m[1]) - 1 : null;
}

/** O bloco é um começo (o principal ou um dos outros)? */
export function ehComeco(id: string): boolean {
  return id === ID_DO_COMECO || posicaoDoOutroComeco(id) !== null;
}

/* A largura do bloco em `editor-do-mapa.css`, e a altura dele com título, resumo e o selo de pendência. */
const LARGURA = 240;
const COLUNA = 300;
const FOLGA = 32;
const ALTURA = 120;
const ORIGEM = { x: 40, y: 40 };

const GRUPO: Record<string, GrupoDoBloco> = {
  acao: 'acao',
  condicao: 'logica',
  escolha: 'logica',
  esperar_tempo: 'espera',
  esperar_evento: 'espera',
  consulta_sql: 'dados',
  para_cada: 'dados',
};

/** O rótulo de uma saída, pela palavra do serviço. */
export function rotuloDaSaida(quando: string | undefined): string | null {
  if (!quando) return null;
  if (quando === 'sim') return 'Sim';
  if (quando === 'nao') return 'Não';
  if (quando === 'padrao') return 'Outro caso';
  if (quando === 'tempo_esgotado') return 'Tempo esgotado';
  if (quando.startsWith('caso:')) return quando.slice(5);
  return quando;
}

/** "2 h", "15 min", "3 dias": a espera numa palavra. */
export function duracao(ms: number): string {
  const minutos = Math.round(ms / 60_000);
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `${horas} h`;
  const dias = Math.round(horas / 24);
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

function curto(texto: string, limite = 60): string {
  const t = texto.trim();
  return t.length > limite ? `${t.slice(0, limite - 1)}…` : t;
}

function tituloDoTipo(tipo: string, blocos: BlocosDoEditor | undefined): string {
  return blocos?.nos.find((n) => n.tipo === tipo)?.titulo ?? tipo;
}

/** O título e o resumo de um bloco do grafo, pelo catálogo do serviço. */
export function descreverNo(
  no: NoDoGrafo,
  blocos: BlocosDoEditor | undefined,
): { titulo: string; resumo: string } {
  switch (no.tipo) {
    case 'acao': {
      const acao = blocos?.acoes.find((a) => a.acao === no.acao);
      const n = Object.keys(no.parametros).length;
      return {
        titulo: acao?.titulo ?? no.acao,
        resumo: n === 0 ? 'Sem dados' : n === 1 ? '1 dado preenchido' : `${n} dados preenchidos`,
      };
    }
    case 'condicao':
    case 'escolha':
      return { titulo: tituloDoTipo(no.tipo, blocos), resumo: curto(no.expressao) || 'Sem regra' };
    case 'esperar_tempo':
      return {
        titulo: tituloDoTipo(no.tipo, blocos),
        resumo: `Espera ${duracao(no.milissegundos)}${no.horario_comercial ? ', no expediente' : ''}`,
      };
    case 'esperar_evento': {
      const evento = blocos?.eventos.find((e) => e.evento === no.evento)?.titulo ?? no.evento;
      return {
        titulo: tituloDoTipo(no.tipo, blocos),
        resumo: no.limite_ms ? `${evento}, até ${duracao(no.limite_ms)}` : evento,
      };
    }
    case 'consulta_sql':
      return {
        titulo: tituloDoTipo(no.tipo, blocos),
        resumo: `Guarda em “${rotuloDoResultado(no.resultado_em)}”`,
      };
    case 'para_cada':
      return {
        titulo: tituloDoTipo(no.tipo, blocos),
        resumo: `${no.corpo.nos.length} ${no.corpo.nos.length === 1 ? 'bloco' : 'blocos'} por linha`,
      };
  }
}

/** As saídas de um bloco: as que o catálogo diz, mais as que o grafo usa. */
function saidasDe(
  no: NoDoGrafo,
  ligacoes: readonly LigacaoDoGrafo[],
  blocos: BlocosDoEditor | undefined,
): string[] {
  const doCatalogo = blocos?.nos.find((n) => n.tipo === no.tipo)?.saidas ?? [];
  const usadas = ligacoes
    .filter((l) => l.de === no.id)
    .map((l) => rotuloDaSaida(l.quando) ?? 'Próximo');
  return [...new Set([...doCatalogo.map((s) => rotuloDaSaida(s) ?? s), ...usadas])];
}

/**
 * Os blocos e as ligações do mapa. `resumoDoComeco` é o "quando" do gatilho,
 * já em palavras (a tela da lista já sabe dizê-lo).
 */
export function montarMapa(
  automacao: Pick<AutomacaoDaApi, 'grafo' | 'condicao'> &
    Partial<Pick<AutomacaoDaApi, 'outros_comecos'>>,
  blocos: BlocosDoEditor | undefined,
  resumoDoComeco: string,
): { blocos: Bloco[]; ligacoes: Ligacao[] } {
  const grafo = automacao.grafo;
  const comeco: Bloco = {
    id: ID_DO_COMECO,
    grupo: 'comeco',
    titulo: 'Começa quando',
    resumo: automacao.condicao
      ? `${resumoDoComeco}. Só se: ${curto(automacao.condicao.expressao, 40)}`
      : resumoDoComeco,
    saidas: [],
    x: 0,
    y: 0,
  };
  if (!grafo) return { blocos: [{ ...comeco, x: ORIGEM.x, y: ORIGEM.y }], ligacoes: [] };

  const outros: Bloco[] = (automacao.outros_comecos ?? []).map((o, i) => ({
    id: idDoOutroComeco(i),
    grupo: 'comeco',
    titulo: 'Também começa quando',
    resumo: o.condicao ? `Só se: ${curto(o.condicao.expressao, 40)}` : '',
    saidas: [],
    x: Number.NaN,
    y: Number.NaN,
  }));
  const ligacoes: Ligacao[] = [
    { id: `${ID_DO_COMECO}->${grafo.inicio}`, de: ID_DO_COMECO, para: grafo.inicio, rotulo: null },
    // `null` entra no primeiro bloco; vazio é o começo que a pessoa desligou.
    ...(automacao.outros_comecos ?? []).flatMap((o, i) => {
      const para = o.entrada === null ? grafo.inicio : o.entrada;
      return para
        ? [{ id: `${idDoOutroComeco(i)}->${para}`, de: idDoOutroComeco(i), para, rotulo: null }]
        : [];
    }),
    ...grafo.ligacoes.map((l, i) => ({
      id: `${l.de}->${l.para}:${l.quando ?? ''}:${i}`,
      de: l.de,
      para: l.para,
      // "padrao" é a continuação: só na escolha ele tem nome, "Outro caso".
      rotulo:
        l.quando === 'padrao' && grafo.nos.find((n) => n.id === l.de)?.tipo !== 'escolha'
          ? null
          : rotuloDaSaida(l.quando),
    })),
  ];

  const lista: Bloco[] = [
    comeco,
    ...outros,
    ...grafo.nos.map((no) => ({
      id: no.id,
      grupo: GRUPO[no.tipo] ?? 'acao',
      ...descreverNo(no, blocos),
      saidas: saidasDe(no, grafo.ligacoes, blocos),
      x: no.posicao?.x ?? Number.NaN,
      y: no.posicao?.y ?? Number.NaN,
    })),
  ];

  const semPosicao = lista.some((b) => !ehComeco(b.id) && Number.isNaN(b.x));
  const organizados = semPosicao ? organizar(lista, ligacoes) : lista;
  // Com as posições guardadas, o começo fica à esquerda do primeiro bloco.
  if (!semPosicao) {
    const primeiro = organizados.find((b) => b.id === grafo.inicio);
    comeco.x = (primeiro?.x ?? ORIGEM.x + COLUNA) - COLUNA;
    comeco.y = primeiro?.y ?? ORIGEM.y;
  }
  // Os outros começos ficam sem posição aqui: quem os desenha é `desenho.ts`, à
  // esquerda do bloco onde entram. Aqui eles só servem de raiz para a árvore.
  return { blocos: organizados, ligacoes };
}

/**
 * A árvore da esquerda para a direita a partir do começo: cada nível numa
 * coluna, o pai no meio dos filhos, e o que não se alcança empilhado embaixo.
 */
export function organizar(blocos: readonly Bloco[], ligacoes: readonly Ligacao[]): Bloco[] {
  const filhos = new Map<string, string[]>();
  for (const l of ligacoes) filhos.set(l.de, [...(filhos.get(l.de) ?? []), l.para]);

  const posicao = new Map<string, { x: number; y: number }>();
  let proximoY = ORIGEM.y;

  // Em profundidade: as folhas ocupam as linhas, e o pai vai para o meio delas.
  const colocar = (id: string, nivel: number, caminho: Set<string>): number => {
    const ja = posicao.get(id);
    if (ja) return ja.y;
    const proximos = (filhos.get(id) ?? []).filter((f) => !caminho.has(f) && !posicao.has(f));
    const x = ORIGEM.x + nivel * COLUNA;
    if (proximos.length === 0) {
      const y = proximoY;
      proximoY += ALTURA + FOLGA;
      posicao.set(id, { x, y });
      return y;
    }
    posicao.set(id, { x, y: Number.NaN });
    const dentro = new Set(caminho).add(id);
    const ys = proximos.map((f) => colocar(f, nivel + 1, dentro));
    const y = (Math.min(...ys) + Math.max(...ys)) / 2;
    posicao.set(id, { x, y });
    return y;
  };
  colocar(ID_DO_COMECO, 0, new Set());
  // O que só um outro começo alcança entra na árvore a partir dele.
  for (const b of blocos) if (posicaoDoOutroComeco(b.id) !== null) colocar(b.id, 0, new Set());

  // Os soltos (que nada alcança), numa fileira embaixo da árvore.
  let xSolto = ORIGEM.x;
  const ySolto = proximoY + FOLGA;
  for (const b of blocos) {
    if (posicao.has(b.id)) continue;
    posicao.set(b.id, { x: xSolto, y: ySolto });
    xSolto += LARGURA + FOLGA;
  }

  return blocos.map((b) => ({ ...b, ...posicao.get(b.id)! }));
}
